// utils/deliveryNotes.js — intranet-018
//
// Mapeo ÚNICO de las respuestas y errores de `/delivery-notes/*` (albaranes). Si cambia el
// contrato de la API, este es el fichero a tocar; el store (`store/deliveryNotes.js`) solo hace
// la I/O y los componentes consumen lo que devuelve. Contrato real:
// `reverence_hotels_api/.claude/docs/delivery-notes-frontend-api-guide.md`.
//
// Todo son funciones puras (salvo la lectura de Blobs, asíncrona): no hay estado ni `$axios`.

/** Modos de generación válidos de `POST /delivery-notes/generate`. */
export const DELIVERY_NOTE_MODES = Object.freeze({ ALL: "all", PENDING: "pending", SELECTED: "selected" });

/** Tope de `purchase_order_ids` del contrato (api-044), tras deduplicar. */
export const MAX_SELECTED_PURCHASE_ORDERS = 500;

/** Nombre de descarga cuando la cabecera no llega, no se expone por CORS o es sospechosa. */
export const DEFAULT_DELIVERY_NOTE_FILENAME = "albaranes.xml";

/** Estados derivados de un pedido en el preview (fuente única para 019/020). */
export const PURCHASE_ORDER_STATUS = Object.freeze({
	GENERATED: "Generado",
	PARTIAL: "Parcial",
	EXCLUDED: "Excluido",
	PENDING: "Pendiente",
});

/** Tipos de error tipado (`error.type`). */
export const DELIVERY_NOTE_ERROR_TYPES = Object.freeze({
	VALIDATION: "validation",
	BAD_REQUEST: "bad_request",
	NO_ORDERS: "no_orders",
	NOTHING_PENDING: "nothing_pending",
	RETRYABLE: "retryable",
	ALL_EXCLUDED: "all_excluded",
	GENERIC: "generic",
	UNEXPECTED_RESPONSE: "unexpected_response",
	EMPTY_RESPONSE: "empty_response",
	NETWORK: "network",
	CANCELLED: "cancelled",
	UNAUTHORIZED: "unauthorized",
	FORBIDDEN: "forbidden",
	DETAIL_UNAVAILABLE: "detail_unavailable",
});

const T = DELIVERY_NOTE_ERROR_TYPES;

const DEFAULT_MESSAGES = {
	[T.VALIDATION]: "Datos de generación no válidos.",
	[T.BAD_REQUEST]: "Petición inválida.",
	[T.NO_ORDERS]: "No hay pedidos aprobados en este rango.",
	[T.NOTHING_PENDING]: "Todo lo de este rango ya está generado.",
	[T.RETRYABLE]: "Otra generación se adelantó; vuelve a intentarlo.",
	[T.ALL_EXCLUDED]: "Todos los albaranes del rango quedaron excluidos por datos incorrectos.",
	[T.GENERIC]: "Error al comunicar con el servidor.",
	[T.UNEXPECTED_RESPONSE]: "El servidor devolvió una respuesta inesperada.",
	[T.EMPTY_RESPONSE]: "El servidor devolvió una respuesta vacía.",
	[T.NETWORK]: "Error de red al comunicar con el servidor.",
	[T.CANCELLED]: "Operación cancelada.",
	[T.UNAUTHORIZED]: "Sesión caducada. Inicia sesión de nuevo.",
	[T.FORBIDDEN]: "No tienes permiso para esta operación.",
	[T.DETAIL_UNAVAILABLE]: "Detalle de la generación no disponible.",
};

// `code` estable de la API → tipo. El status solo apoya cuando no hay `code`.
const TYPE_BY_CODE = {
	bad_request: T.BAD_REQUEST,
	no_approved_orders: T.NO_ORDERS,
	nothing_pending: T.NOTHING_PENDING,
	generation_conflict: T.RETRYABLE,
	all_excluded: T.ALL_EXCLUDED,
	internal_error: T.GENERIC,
};

const TYPE_BY_STATUS = {
	400: T.BAD_REQUEST,
	404: T.NO_ORDERS,
	409: T.RETRYABLE,
	422: T.ALL_EXCLUDED,
};

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const XML_START = /^<(\?xml|Albaranes)/i;
const SNIFF_BYTES = 256;

const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const isPositiveInt = (value) => Number.isInteger(value) && value > 0;
const isCount = (value) => Number.isInteger(value) && value >= 0;
const toCount = (value) => (isPositiveInt(value) ? value : 0);
const asString = (value) => (typeof value === "string" ? value : "");

/**
 * Crea un error tipado. Se evita `class extends Error` a propósito: con el target ES5 de Babel
 * pierde `instanceof`; se identifica con `isDeliveryNoteError`.
 * @param {string} type Uno de `DELIVERY_NOTE_ERROR_TYPES`.
 * @param {{status?: number, code?: string, message?: string, details?: Array}} [info]
 * @returns {Error & {type: string, status: ?number, code: string, details: Array}}
 */
export function createDeliveryNoteError(type, { status = null, code = "", message = "", details = [] } = {}) {
	const error = new Error(message || DEFAULT_MESSAGES[type] || DEFAULT_MESSAGES[T.GENERIC]);
	error.name = "DeliveryNoteError";
	error.type = type;
	error.status = status;
	error.code = code;
	error.details = details;
	return error;
}

/** @param {*} error @returns {boolean} `true` si lo creó `createDeliveryNoteError`. */
export function isDeliveryNoteError(error) {
	return Boolean(error) && error.name === "DeliveryNoteError" && typeof error.type === "string";
}

const validationError = (message) => createDeliveryNoteError(T.VALIDATION, { message });

/**
 * Normaliza una fecha a `YYYY-MM-DD` sin desfase de zona (un `Date` usa sus getters locales).
 * @param {string|Date} value
 * @returns {?string} `null` si no es una fecha válida.
 */
export function toDateString(value) {
	if (value instanceof Date) {
		if (Number.isNaN(value.getTime())) return null;
		const pad = (n) => String(n).padStart(2, "0");
		return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
	}
	if (typeof value !== "string" || !DATE_PATTERN.test(value)) return null;
	const [year, month, day] = value.split("-").map(Number);
	const parsed = new Date(Date.UTC(year, month - 1, day));
	const valid = parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day;
	return valid ? value : null;
}

function requireRange({ date_from: dateFrom, date_to: dateTo } = {}) {
	const from = toDateString(dateFrom);
	const to = toDateString(dateTo);
	if (!from || !to) throw validationError("Rango de fechas no válido (se espera YYYY-MM-DD).");
	return { date_from: from, date_to: to };
}

function requireValidIds(ids) {
	if (!Array.isArray(ids) || ids.length === 0) {
		throw validationError("Selecciona al menos un pedido.");
	}
	if (!ids.every(isPositiveInt)) throw validationError("Identificadores de pedido no válidos.");
	const unique = Array.from(new Set(ids));
	if (unique.length > MAX_SELECTED_PURCHASE_ORDERS) {
		throw validationError(`Demasiados pedidos seleccionados (máximo ${MAX_SELECTED_PURCHASE_ORDERS}).`);
	}
	return unique;
}

/**
 * Construye y valida el body de `POST /delivery-notes/generate`. `mode` es obligatorio y exacto
 * (nunca se omite); `purchase_order_ids` solo viaja con `selected`.
 * @param {{date_from: (string|Date), date_to: (string|Date), mode: string, purchase_order_ids?: number[]}} params
 * @returns {{date_from: string, date_to: string, mode: string, purchase_order_ids?: number[]}}
 * @throws {Error} Error tipado `validation` (no se llama a la API).
 */
export function buildGenerateRequest(params = {}) {
	const range = requireRange(params);
	const { mode } = params;
	if (!Object.values(DELIVERY_NOTE_MODES).includes(mode)) {
		throw validationError("Modo de generación no válido.");
	}
	const body = { ...range, mode };
	if (mode === DELIVERY_NOTE_MODES.SELECTED) {
		body.purchase_order_ids = requireValidIds(params.purchase_order_ids);
	}
	return body;
}

/**
 * Query de `GET /delivery-notes/preview`; `only_pending` solo si es `true`.
 * @param {{date_from: (string|Date), date_to: (string|Date), only_pending?: boolean}} params
 * @returns {{date_from: string, date_to: string, only_pending?: boolean}}
 * @throws {Error} Error tipado `validation`.
 */
export function buildPreviewParams(params = {}) {
	const query = requireRange(params);
	if (params.only_pending === true) query.only_pending = true;
	return query;
}

function getHeader(headers, name) {
	if (!isObject(headers)) return undefined;
	const key = Object.keys(headers).find((candidate) => candidate.toLowerCase() === name);
	return key === undefined ? undefined : headers[key];
}

function isSafeFilename(name) {
	return name !== "" && !/[\\/\u0000-\u001f]/.test(name) && !name.includes("..");
}

/**
 * Extrae un nombre de archivo seguro de `Content-Disposition` (`filename*=` tiene prioridad).
 * Vacío, con `/`, `\`, `..` o mal codificado → `DEFAULT_DELIVERY_NOTE_FILENAME`.
 * @param {?string} contentDisposition
 * @returns {string}
 */
export function sanitizeFilename(contentDisposition) {
	if (typeof contentDisposition !== "string") return DEFAULT_DELIVERY_NOTE_FILENAME;

	let candidate = "";
	const extended = contentDisposition.match(/filename\*\s*=\s*[\w-]*'[^']*'([^;]*)/i);
	if (extended) {
		try {
			candidate = decodeURIComponent(extended[1].trim());
		} catch (error) {
			return DEFAULT_DELIVERY_NOTE_FILENAME;
		}
	} else {
		const plain = contentDisposition.match(/filename\s*=\s*(?:"([^"]*)"|([^;]*))/i);
		if (plain) candidate = (plain[1] !== undefined ? plain[1] : plain[2]).trim();
	}

	return isSafeFilename(candidate) ? candidate : DEFAULT_DELIVERY_NOTE_FILENAME;
}

/**
 * Parsea `X-Delivery-Note-Summary`. Malformada o con contadores inválidos → `null`.
 * @param {?string} raw
 * @returns {?{deliveryNoteId: number, generated: number, excluded: number, ignored: number, alreadyGenerated: number}}
 */
export function parseSummaryHeader(raw) {
	if (typeof raw !== "string" || raw.trim() === "") return null;
	let data;
	try {
		data = JSON.parse(raw);
	} catch (error) {
		return null;
	}
	if (!isObject(data)) return null;

	const optionalCount = (value) => (value === undefined ? 0 : value);
	const ignored = optionalCount(data.ignored);
	const alreadyGenerated = optionalCount(data.already_generated);
	const valid =
		isPositiveInt(data.delivery_note_id) &&
		isCount(data.generated) &&
		isCount(data.excluded) &&
		isCount(ignored) &&
		isCount(alreadyGenerated);
	if (!valid) return null;

	return {
		deliveryNoteId: data.delivery_note_id,
		generated: data.generated,
		excluded: data.excluded,
		ignored,
		alreadyGenerated,
	};
}

function parseDeliveryNoteId(raw) {
	if (typeof raw !== "string" && typeof raw !== "number") return null;
	const text = String(raw).trim();
	if (!/^\d+$/.test(text)) return null;
	const id = Number(text);
	return isPositiveInt(id) ? id : null;
}

function readBlobAsText(blob) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(String(reader.result));
		reader.onerror = () => reject(reader.error);
		reader.readAsText(blob);
	});
}

const isBlob = (value) => typeof Blob !== "undefined" && value instanceof Blob;

/**
 * Interpreta un `200` de `generate`: valida que el cuerpo sea XML (no HTML de proxy ni JSON ni
 * vacío) y lee id/resumen/nombre de forma independiente; las cabeceras ausentes degradan sin
 * romper la descarga.
 * @param {{data: Blob, headers: Object}} response Respuesta axios con `responseType: 'blob'`.
 * @returns {Promise<{blob: Blob, filename: string, deliveryNoteId: ?number, summary: ?Object}>}
 * @throws {Error} Error tipado `empty_response` o `unexpected_response`.
 */
export async function parseGenerateResponse(response) {
	const blob = response && response.data;
	if (blob === undefined || blob === null || (isBlob(blob) && blob.size === 0)) {
		throw createDeliveryNoteError(T.EMPTY_RESPONSE);
	}
	if (!isBlob(blob)) throw createDeliveryNoteError(T.UNEXPECTED_RESPONSE);

	const head = (await readBlobAsText(blob.slice(0, SNIFF_BYTES))).replace(/^﻿/, "").trimStart();
	if (head === "") throw createDeliveryNoteError(T.EMPTY_RESPONSE);
	if (!XML_START.test(head)) throw createDeliveryNoteError(T.UNEXPECTED_RESPONSE);

	const headers = response.headers;
	return {
		blob,
		filename: sanitizeFilename(getHeader(headers, "content-disposition")),
		deliveryNoteId: parseDeliveryNoteId(getHeader(headers, "x-delivery-note-id")),
		summary: parseSummaryHeader(getHeader(headers, "x-delivery-note-summary")),
	};
}

function mapExclusion(raw) {
	return {
		establishmentCode: raw.establishment_code ?? null,
		providerCode: raw.provider_code ?? "",
		albaranNumber: raw.albaran_number ?? "",
		reason: raw.reason ?? "",
		detail: raw.detail ?? "",
	};
}

function mapIgnoredId(raw) {
	return { purchaseOrderId: raw.purchase_order_id, reason: raw.reason ?? "" };
}

// Los `details` de un error son exclusiones (`all_excluded`) o ids descartados (`selected`
// sin ids aprovechables); se distinguen por la forma.
function mapErrorDetails(details) {
	if (!Array.isArray(details)) return [];
	return details
		.filter((entry) => entry !== null && entry !== undefined)
		.map((entry) => {
			if (!isObject(entry)) return { detail: String(entry) };
			return "purchase_order_id" in entry ? mapIgnoredId(entry) : mapExclusion(entry);
		});
}

async function readErrorBody(data) {
	if (isBlob(data)) {
		try {
			const parsed = JSON.parse(await readBlobAsText(data));
			return isObject(parsed) ? parsed : null;
		} catch (error) {
			return null;
		}
	}
	return isObject(data) ? data : null;
}

const isCancel = (error) =>
	Boolean(error) && (error.__CANCEL__ === true || error.code === "ERR_CANCELED" || error.name === "CanceledError");

/**
 * Convierte cualquier fallo de axios en un error tipado. Clasifica por `code` del cuerpo (que
 * puede venir como Blob JSON) y usa el status solo de apoyo; 401/403 y red/cancelación tienen
 * tipo propio. No lanza nunca por un cuerpo no parseable ni loguea el cuerpo.
 * @param {*} error
 * @returns {Promise<Error>} Error tipado (`isDeliveryNoteError`).
 */
export async function classifyError(error) {
	if (isDeliveryNoteError(error)) return error;
	if (isCancel(error)) return createDeliveryNoteError(T.CANCELLED);

	const response = error && error.response;
	if (!response) return createDeliveryNoteError(T.NETWORK);

	const { status } = response;
	if (status === 401) return createDeliveryNoteError(T.UNAUTHORIZED, { status });
	if (status === 403) return createDeliveryNoteError(T.FORBIDDEN, { status });

	const body = (await readErrorBody(response.data)) || {};
	const code = asString(body.code);
	const type = code ? TYPE_BY_CODE[code] || T.GENERIC : TYPE_BY_STATUS[status] || T.GENERIC;

	return createDeliveryNoteError(type, {
		status,
		code,
		message: asString(body.error),
		details: mapErrorDetails(body.details),
	});
}

/**
 * Regla única del estado de un pedido del preview: `Generado` (todos los provider_orders
 * generados), `Parcial` (algo generado, cuenta como ya generado aunque tenga exclusión),
 * `Excluido` (nada generado y con motivo) o `Pendiente`.
 * @param {?{generated?: boolean, generated_count?: number, total_provider_orders?: number, reason?: string}} item
 * @returns {string} Un valor de `PURCHASE_ORDER_STATUS`.
 */
export function derivePurchaseOrderStatus(item) {
	const source = item || {};
	const generated = toCount(source.generated_count);
	const total = toCount(source.total_provider_orders);
	const allGenerated =
		(total > 0 && generated >= total) || (total === 0 && generated === 0 && source.generated === true);

	if (allGenerated) return PURCHASE_ORDER_STATUS.GENERATED;
	if (generated > 0) return PURCHASE_ORDER_STATUS.PARTIAL;
	if (source.reason) return PURCHASE_ORDER_STATUS.EXCLUDED;
	return PURCHASE_ORDER_STATUS.PENDING;
}

/**
 * Mapea un pedido del preview a camelCase con valores por defecto.
 * `alreadyGenerated` es `true` para `Generado` y `Parcial` (hay que pedir confirmación).
 * @param {Object} raw Elemento de `items` de `GET /delivery-notes/preview`.
 * @returns {{purchaseOrderId: number, status: string, alreadyGenerated: boolean, generatedCount: number,
 *   pendingCount: number, totalProviderOrders: number, reason: string, detail: string, exclusions: Array}}
 */
export function mapPreviewItem(raw) {
	const status = derivePurchaseOrderStatus(raw);
	return {
		purchaseOrderId: raw.purchase_order_id,
		status,
		alreadyGenerated: status === PURCHASE_ORDER_STATUS.GENERATED || status === PURCHASE_ORDER_STATUS.PARTIAL,
		generatedCount: toCount(raw.generated_count),
		pendingCount: toCount(raw.pending_count),
		totalProviderOrders: toCount(raw.total_provider_orders),
		reason: asString(raw.reason),
		detail: asString(raw.detail),
		exclusions: Array.isArray(raw.exclusions) ? raw.exclusions.filter(isObject).map(mapExclusion) : [],
	};
}

/**
 * Mapea el cuerpo del preview. Rango vacío, `items` nulo o ausente → lista vacía; descarta
 * elementos sin `purchase_order_id` entero.
 * @param {?{items?: Array}} data
 * @returns {{items: Array}}
 */
export function mapPreviewResponse(data) {
	const items = isObject(data) && Array.isArray(data.items) ? data.items : [];
	return {
		items: items.filter((item) => isObject(item) && Number.isInteger(item.purchase_order_id)).map(mapPreviewItem),
	};
}

/**
 * Mapea el detalle de una generación (`DeliveryNote` de `GET /delivery-notes[/:page]`): los tres
 * grupos son siempre listas.
 * @param {Object} raw
 * @returns {{id: number, exclusions: Array, ignoredIds: Array, alreadyGenerated: number[]}}
 */
export function mapDeliveryNote(raw) {
	const source = raw || {};
	return {
		id: source.ID,
		exclusions: Array.isArray(source.exclusions) ? source.exclusions.filter(isObject).map(mapExclusion) : [],
		ignoredIds: Array.isArray(source.ignored_ids) ? source.ignored_ids.filter(isObject).map(mapIgnoredId) : [],
		alreadyGenerated: Array.isArray(source.already_generated) ? source.already_generated : [],
	};
}
